import { Button, Center } from "@mantine/core";
import { EmptyState } from "@mountaya/studio-react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import {
  IconArrowRotateClockwiseOutline18,
  IconTriangleWarningOutline18,
} from "nucleo-ui-outline-18";
import { useTranslation } from "react-i18next";

import { ICON_SIZE } from "@mountayaapp/theme";

/**
 * RouteError is the application-wide error screen TanStack Router renders when a
 * route's loader rejects or its component throws — wired as the router's
 * defaultErrorComponent. It frames the failure in the shared EmptyState block and
 * offers a retry that calls the router-supplied reset to re-run the failed route,
 * so a transient failure (a dropped request, a flaky load) recovers in place
 * without a full reload. A generic message is shown rather than the raw error so a
 * stack trace never reaches the user.
 */
export const RouteError = ({ reset }: ErrorComponentProps) => {
  const { t } = useTranslation();

  return (
    <Center mih="100vh">
      <EmptyState
        action={
          <Button
            leftSection={<IconArrowRotateClockwiseOutline18 size={ICON_SIZE.sm} />}
            onClick={reset}
            variant="default"
          >
            {t("routeError.retry")}
          </Button>
        }
        description={t("routeError.description")}
        icon={<IconTriangleWarningOutline18 size={ICON_SIZE.xl} />}
        title={t("routeError.title")}
      />
    </Center>
  );
};
